import * as React from 'react'
import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
  CheckOutlined,
  CloseOutlined,
  StopOutlined,
  UnlockOutlined,
} from '@ant-design/icons'

import { useBonus150Widget } from './hooks'
import { updateParticipantFx } from './model'
import { Booster, BonusDay } from './types'
import { BanModal } from '../../../promo/bonus150/participants/widgets/BanModal/component'
import { UnbanModal } from '../../../promo/bonus150/participants/widgets/UnbanModal/component'
import { getNumberFormat } from '../../../../libs/utils'
import { formatDate } from '../../../../libs/formatDates'

const { Text } = Typography

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  close: 'default',
  disqualified: 'red',
  'auto-disqualified': 'volcano',
  auto_banned: 'orange',
}

const DAY_STATUS_COLOR: Record<string, string> = {
  active: 'green',
  ended: 'red',
}

const BOOSTER_STATUS_COLOR: Record<string, string> = {
  new: 'default',
  claimed: 'green',
}

const EM_DASH = '\u2014'

const formatOrDash = (value: string | null | undefined | number) =>
  value ? formatDate(value) : EM_DASH

const formatNumberOrDash = (
  value: string | number | null | undefined,
  fixed = 2,
) => {
  if (value === null || value === undefined || value === '') return EM_DASH
  return getNumberFormat(value, fixed)
}

export const Bonus150Widget: React.FC = () => {
  const {
    bonus150,
    loading,
    error,
    updateLoading,
    banParticipant,
    unbanParticipant,
  } = useBonus150Widget()

  const [banModal, setBanModal] = useState(false)
  const [unbanModal, setUnbanModal] = useState(false)

  useEffect(() => {
    const unsub = updateParticipantFx.doneData.watch(() => {
      setBanModal(false)
      setUnbanModal(false)
    })
    const unsubFail = updateParticipantFx.failData.watch(() => {
      message.error('Error. Please try again.')
    })
    return () => {
      unsub()
      unsubFail()
    }
  }, [])

  if (loading && !bonus150) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    )
  }

  if (error.code && error.code !== 404) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load Bonus 150"
        description={error.message}
      />
    )
  }

  if (!bonus150) {
    return (
      <Card>
        <Empty description="Client is not a Bonus 150 participant" />
      </Card>
    )
  }

  const status = bonus150.status
  const isAutoBanned = status === 'auto_banned'
  const isBan = status === 'banned'
  const bonusFinishedTs =
    isBan || isAutoBanned ? bonus150.bannedTs : bonus150.endedTs

  const boosterColumns: ColumnsType<Booster> = [
    {
      title: 'Type',
      dataIndex: 'boosterType',
      render: (val: string) => (
        <Text style={{ textTransform: 'capitalize' }}>{val}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (val: string) => (
        <Tag
          color={BOOSTER_STATUS_COLOR[val] || 'default'}
          style={{ textTransform: 'capitalize' }}
        >
          {val}
        </Tag>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      width: 120,
      render: (val: string) => formatNumberOrDash(val),
    },
    {
      title: 'Claimed At',
      dataIndex: 'claimedAt',
      width: 170,
      render: (val: string | null) => formatOrDash(val),
    },
  ]

  const dayColumns: ColumnsType<BonusDay> = [
    { title: 'Day', dataIndex: 'dayNumber', width: 70, align: 'center' },
    {
      title: 'Date',
      dataIndex: 'date',
      width: 170,
      render: (date: string) => formatOrDash(date),
    },
    {
      title: 'Instruments',
      dataIndex: 'instruments',
      render: (instruments: string) => instruments || EM_DASH,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (dayStatus: string) => (
        <Tag
          color={DAY_STATUS_COLOR[dayStatus] || 'default'}
          style={{ textTransform: 'capitalize' }}
        >
          {dayStatus}
        </Tag>
      ),
    },
    {
      title: 'Conditions',
      dataIndex: 'isConditionsCompleted',
      width: 110,
      align: 'center',
      render: (done: boolean) =>
        done ? (
          <CheckOutlined style={{ color: '#22c55e' }} />
        ) : (
          <CloseOutlined style={{ color: '#ef4444' }} />
        ),
    },
  ]

  return (
    <Flex vertical gap={16}>
      <Card
        title={
          <Flex align="center" gap={10}>
            <Text strong style={{ fontSize: 16 }}>
              Bonus 150
            </Text>
            <Tag
              color={STATUS_COLOR[status] || 'default'}
              style={{ fontSize: 11, textTransform: 'capitalize', margin: 0 }}
            >
              {status}
            </Tag>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Bonus account ID: {bonus150.bonusAccountId}
            </Text>
          </Flex>
        }
        extra={
          <Space size={8}>
            {!isAutoBanned && !isBan && (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={() => setBanModal(true)}
              >
                Disqualify
              </Button>
            )}
            {isAutoBanned && (
              <Button
                icon={<UnlockOutlined />}
                onClick={() => setUnbanModal(true)}
              >
                Cancel Auto-Disqualification
              </Button>
            )}
          </Space>
        }
      >
        <Descriptions
          title="Bonus Information"
          column={2}
          size="small"
          bordered
          items={[
            {
              key: 'withdrawalType',
              label: 'Selected Profit Withdrawal Type',
              children: bonus150.withdrawalType ? (
                <Tag color="blue" style={{ textTransform: 'capitalize' }}>
                  {bonus150.withdrawalType}
                </Tag>
              ) : (
                <Text type="secondary">not</Text>
              ),
            },
            {
              key: 'sumCurrentWithdrawalLots',
              label: 'Current Traded Lots for Withdrawal',
              children: formatNumberOrDash(bonus150.sumCurrentWithdrawalLots),
            },
            {
              key: 'sumWithdrawalLots',
              label: 'Traded Lots for Withdrawal',
              children: formatNumberOrDash(bonus150.sumWithdrawalLots),
            },
            {
              key: 'sumBoostersLots',
              label: 'Traded Lots for Booster',
              children: formatNumberOrDash(bonus150.sumBoostersLots),
            },
            {
              key: 'coefficient',
              label: 'Coefficient',
              children: formatNumberOrDash(bonus150.coefficient),
            },
            {
              key: 'withdrawalAmount',
              label: 'Calculated Withdrawal Amount',
              children: formatNumberOrDash(bonus150.withdrawalAmount),
            },
          ]}
        />
      </Card>

      <Card size="small" title="Daily Bonus Status">
        <Table<BonusDay>
          rowKey="id"
          size="small"
          columns={dayColumns}
          dataSource={bonus150.days}
          pagination={false}
          locale={{ emptyText: 'No data' }}
        />
      </Card>

      <Card size="small" title="Booster Claim Status">
        <Table<Booster>
          rowKey="id"
          size="small"
          columns={boosterColumns}
          dataSource={bonus150.boosters}
          pagination={false}
          locale={{ emptyText: 'No boosters' }}
        />
      </Card>

      <Card size="small">
        <Descriptions
          title="Trading Account Information"
          column={2}
          size="small"
          bordered
          items={[
            {
              key: 'mtLogin',
              label: 'MT Login',
              children: bonus150.account
                ? `${bonus150.account.mtLogin} (${bonus150.account.accountPlatform})`
                : EM_DASH,
            },
            {
              key: 'openedAt',
              label: 'Bonus Account Opening Date & Time',
              children: bonus150.account
                ? formatOrDash(bonus150.registeredTs)
                : EM_DASH,
            },
            {
              key: 'closedAt',
              label: 'Bonus Account Closing Date & Time',
              children: formatOrDash(bonusFinishedTs),
            },
            {
              key: 'balance',
              label: 'Balance',
              children: formatNumberOrDash(
                bonus150.account?.balance ?? bonus150.balance,
              ),
            },
            {
              key: 'sumBonusLots',
              label: 'Lots on Bonus Account',
              children: formatNumberOrDash(bonus150.sumBonusLots),
            },
            {
              key: 'sumBonusProfit',
              label: 'Profit',
              children: formatNumberOrDash(bonus150.sumBonusProfit),
            },
          ]}
        />
      </Card>

      <BanModal
        open={banModal}
        participant={bonus150}
        loading={updateLoading}
        onClose={() => setBanModal(false)}
        onSubmit={(banReason) =>
          banParticipant({ id: bonus150.client.clientId, banReason })
        }
      />
      <UnbanModal
        open={unbanModal}
        participant={bonus150}
        loading={updateLoading}
        onClose={() => setUnbanModal(false)}
        onSubmit={(banReason) =>
          unbanParticipant({ id: bonus150.client.clientId, banReason })
        }
      />
    </Flex>
  )
}
